import React, { useEffect, useState } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	wrapper: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'end',
		position: 'relative',
	},
	pill: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'end',
		borderRadius: token('radius.xxlarge'),
		backgroundColor: token('color.background.neutral.bold'),
		// @ts-expect-error TS2322 raw box-shadow string not in ADS shadow token union
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		boxShadow: '0 0 1px 0 rgba(30, 31, 33, 0.31), 0 8px 12px 0 rgba(30, 31, 33, 0.15)',
		transform: 'scale(1)',
		transformOrigin: 'right center',
		transition: 'transform 0.2s ease-in-out',
		willChange: 'transform',
	},
	overlay: {
		display: 'none',
		position: 'absolute',
		right: token('space.0'),
		// @ts-expect-error TS2322 cssMap does not accept percentage strings for bottom
		bottom: '100%',
		marginBottom: token('space.100'),
		zIndex: 1,
	},
	showOverlayForQuickInputFocus: {
		// @ts-expect-error TS2353 relational selectors are supported at runtime
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- keeps the overlay scoped to the quick-input interaction rather than any focus within the expander
		'&:has([data-quick-input]:focus-within, [data-quick-input-overlay]:hover, [data-quick-input-overlay]:focus-within) [data-quick-input-overlay]':
			{
				display: 'block',
			},
	},
	hoverScale: {
		'&:hover': {
			transform: 'scale(1.125)',
		},
		'&:active': {
			transform: 'scale(1.125)',
		},
	},
	expandPill: {
		display: 'grid',
		alignSelf: 'stretch',
		gridTemplateColumns: '0fr',
		overflow: 'hidden',
		transition: 'grid-template-columns 0.2s ease-in-out',
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'none',
		},
	},
	expandPillExpanded: {
		gridTemplateColumns: '1fr',
	},
	expandPillClip: {
		display: 'flex',
		whiteSpace: 'nowrap',
		minWidth: 0,
		// Hidden content remains mounted so quick-input drafts survive collapse, but
		// cannot receive focus from Jira's modal focus management.
		visibility: 'hidden',
		transform: 'translateX(100%)',
		willChange: 'transform',
		// Let the collapse animation finish before hiding the content. Expansion
		// overrides the delay above so the content is visible immediately.
		transition: 'transform 0.2s ease-in-out, visibility 0s linear 0.2s',
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'none',
		},
	},
	expandPillClipExpanded: {
		visibility: 'visible',
		transform: 'translateX(0)',
		transitionDelay: '0s',
	},
});

type RovoButtonExpanderProps = {
	children: React.ReactNode;
	expandedContent?: React.ReactNode;
	isExpansionSuppressed?: boolean;
	onExpandedChange?: (expanded: boolean) => void;
	overlayContent?: React.ReactNode;
	testId?: string;
};

export function RovoButtonExpander({
	children,
	expandedContent,
	isExpansionSuppressed = false,
	onExpandedChange,
	overlayContent,
	testId,
}: RovoButtonExpanderProps): React.JSX.Element {
	const [isHovered, setIsHovered] = useState(false);
	const [isFocusWithin, setIsFocusWithin] = useState(false);
	const isExpanded = !isExpansionSuppressed && (isHovered || isFocusWithin);

	useEffect(() => {
		onExpandedChange?.(isExpanded);
	}, [isExpanded, onExpandedChange]);

	return (
		<Box
			xcss={cx(styles.wrapper, !!overlayContent && styles.showOverlayForQuickInputFocus)}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			onFocus={() => setIsFocusWithin(true)}
			onBlur={(event: React.FocusEvent<HTMLElement>) => {
				if (!event.currentTarget.contains(event.relatedTarget as Node)) {
					setIsFocusWithin(false);
				}
			}}
		>
			{isExpanded && overlayContent && (
				<Box xcss={styles.overlay} data-quick-input-overlay>
					{overlayContent}
				</Box>
			)}
			<Box testId={testId} xcss={cx(styles.pill, styles.hoverScale)}>
				<Box
					xcss={cx(styles.expandPill, isExpanded && styles.expandPillExpanded)}
					data-expandable-pill
				>
					<Box
						xcss={cx(styles.expandPillClip, isExpanded && styles.expandPillClipExpanded)}
						data-expandable-pill-clip
					>
						{expandedContent}
					</Box>
				</Box>
				{children}
			</Box>
		</Box>
	);
}
